<script>
    import R, {LOADER} from "../lib/requests";
    import app from "../stores/app";
    import members from "../stores/members";
    import Notifier from "../stores/notifier";
    import Alert from "../stores/alerts.js";
    import {onDestroy} from 'svelte';
    import {setFocusTo} from "../lib/lib";
    import PageHeader from "../components/PageHeader.svelte";
    import Breadcrumb from "../components/Breadcrumb.svelte";
    import PageNotFound from "../components/pages/PageNotFound.svelte";
    import MemberLog from "./MemberLog.svelte";
    import Tabs from "../components/tabs/Tabs.svelte";
    import PasswordControl from "../components/controls/PasswordControl.svelte";
    import PhoneControl from "../components/controls/PhoneControl.svelte";
    import PointsHistory from "./PointsHistory.svelte";
    import CalendarControl from "../components/controls/CalendarControl.svelte";

    const breadcrumb = [
        {id: 'members', name: 'Members', link: `/app/members`}
    ];


    let tabTitles = ['MEMBER'];

    export let memberId = null;

    let member = null;
    let pageTitle = '';
    let changePassword = false;
    let tabPoints;
    let tabLog;

    const initialize = () => {
        if (memberId == 0) {
            member = {
                id: 0,
                code: '',
                abbr: 'mr',
                lastname: '',
                firstname: '',
                birthday: null,
                country_id: null,
                phone: '',
                email: '',
                password: null,
                status_id: '1',
                teatro_pass_expire_at: null,
            };
            $members.memberIsDirty = false;
            pageTitle = "New member";
            setFocusTo("lastname");
        } else {
            pageTitle = "Edit member";
            tabTitles = ['MEMBER', 'POINTS', 'CHANGE LOG'];
            load();
        }
    }

    const load = () => {
        const onSuccess = result => {
            member = result;
            $members.memberIsDirty = false;
            setFocusTo("lastname");
        }

        const onFailure = err => {
            if (err.code && err.code === 'not_found') {
                memberId = -1;
            }
        };

        R.handle(R.member.details, memberId, onSuccess, onFailure, LOADER.PAGE);
    }

    const save = () => {
        const onSuccess = result => {
            if (member.id == 0) {
                Notifier.showMessage("Member created");
            } else {
                Notifier.showMessage("Member updated");
            }
            member.id = result.id;
            changePassword = false;
            delete(member.new_password);
            pageTitle = "Edit member";
            $members.changed = true;
        }

        const onFailure = err => {
            let fields = {
                phone: 'Phone',
                email: 'Email',
            }
            let field = null;
            let message = '';
            if (err.code) {
                if (err.code === 'same_object_identifier') {
                    message = `There is already a member account with the email "${member.email}"!`;
                }
                if (err.code === 'field_required') {
                    field = err.params.field;
                    message = `Field "${fields[field]}" is required!`;
                }
                if (err.code === 'field_invalid') {
                    field = err.params.field;
                    message = `Field "${fields[field]}" does not contain a valid value!`;
                }
            }

            if (!message) {
                message = err.message;
                field = err.params && err.params.field ? err.params.field : null;
            }

            Alert.warning("Save member", message, () => {
                if (field) {
                    setFocusTo(field);
                }
            });
        };

        member.lastname = member.lastname.trim();
        if (!member.lastname) {
            Notifier.showError("Type the member's last name!");
            setFocusTo("lastname");
            return;
        }

        member.email = member.email.trim();


        let action = member.id == 0 ? 'create' : 'edit';
        R.handle(R.member[action], member, onSuccess, onFailure, LOADER.POPUP);
    }

    const onTogglePassword = () => {
        changePassword = !changePassword;
    }

    const onTabChange = ({detail}) => {
        if (detail.index == 2) {
            tabPoints.initialize();
        } else if (detail.index == 3) {
            tabLog.initialize();
        }
    }

    const beforeUnloadListener = (event) => {
        event.preventDefault();
        return (event.returnValue = "");
    };

    onDestroy(() => {
        $members.memberIsDirty = false;
        removeEventListener("beforeunload", beforeUnloadListener, {capture: true});
    });

    initialize();
</script>

<Breadcrumb {breadcrumb}/>
<PageHeader title={ pageTitle } subtitle={member && member.code ? `Code: ${member.code}` : '' }/>
{#if member}
    <div class="row">
        <div class="col-xl-6">
            <Tabs titles={tabTitles} cssClass="mb-5" on:click={ onTabChange }>
                <div slot="tab1">
                    <div class="row">
                        <div class="col-xl-6 col-lg-6 col-md-6 form-group">
                            <label>Last name</label>
                            <div class="input-group">
                                <select class="form-select member-title" bind:value="{ member.abbr }">
                                    'Mr', 'Sir', 'Mrs', 'Miss'
                                    <option value="Mr.">Mr.</option>
                                    <option value="Sir">Sir</option>
                                    <option value="Mrs">Mrs</option>
                                    <option value="Miss">Miss</option>
                                </select>
                                <input type="text" class="form-control member-lastname" id="lastname"
                                       bind:value="{ member.lastname }">
                            </div>
                        </div>
                        <div class="col-xl-6 col-lg-6 col-md-6 form-group">
                            <label>First name</label>
                            <input type="text" class="form-control" bind:value="{ member.firstname }">
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-xl-4 col-lg-4 col-md-4 form-group">
                            <label>Birthday</label>
                            <input type="date" class="form-control" bind:value="{ member.birthday }">
                        </div>
                        <div class="col-xl-4 col-lg-4 col-md-4 form-group">
                            <label>Nationality</label>
                            <select class="form-select" bind:value="{ member.country_id }">
                                <option value=""></option>
                                {#each $app.countries as country}
                                    <option value="{country.id}">{ country.name }</option>
                                {/each}
                            </select>
                        </div>
                        <div class="col-xl-4 col-lg-4 col-md-4 form-group">
                            <label>Phone</label>
                            <PhoneControl id="phone" bind:value="{ member.phone }" />
                        </div>
                    </div>

                    <legend>Account</legend>
                    <div class="row">
                        <div class="col-xl-6 col-lg-6 col-md-6 form-group">
                            <label>Email</label>
                            <input type="email" id="email" class="form-control" bind:value="{ member.email }"
                                   autocomplete="off">
                        </div>
                        <div class="col-xl-6 col-lg-6 col-md-6">
                            {#if member.id > 0}
                                {#if !changePassword}
                                    <button class="btn btn-link px-0 mt-xl-4 mt-lg-4 mt-md-4"
                                            on:click={ onTogglePassword }>Change
                                        password
                                    </button>
                                {:else}
                                    <button class="btn btn-link px-0 mt-xl-4 mt-lg-4 mt-md-4"
                                            on:click={ onTogglePassword }>Do not
                                        change
                                        password
                                    </button>
                                {/if}
                            {/if}
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-xl-6 col-lg-6 col-md-6 form-group">
                            {#if member.id == 0}
                                <label>Account password</label>
                                <PasswordControl id="password" bind:value="{ member.password }"/>
                            {:else if changePassword}
                                <label>New password</label>
                                <PasswordControl id="password" bind:value="{ member.new_password }"/>
                            {/if}
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-xl-6 col-lg-6 col-md-6 form-group">
                            <select class="form-select" bind:value="{ member.status_id }">
                                <option value="0">Unverified account</option>
                                <option value="1">Active account</option>
                                <option value="2">Disabled account</option>
                            </select>
                        </div>
                    </div>
                    <legend>Teatro Pass</legend>
                    <div class="row">
                        <div class="col-xl-4 col-lg-4 col-md-4 form-group">
                            <label>Member has Teatro Pass until</label>
                            <CalendarControl bind:date={member.teatro_pass_expire_at} />
                        </div>
                    </div>
                    <hr>
                    <div class="text-end">
                        <button class="btn btn-primary px-4" on:click={ save }>SAVE</button>
                    </div>
                </div>

                <div slot="tab2">
                    <PointsHistory bind:this={tabPoints} memberId="{ member.id }" totalPoints="{ member.total_points }"/>
                </div>

                <div slot="tab3">
                    <MemberLog bind:this={tabLog} memberId="{ member.id }"/>
                </div>
            </Tabs>
        </div>
    </div>
{/if}

{#if memberId == -1}
    <PageNotFound message="Member not found!"/>
{/if}

<style>
    .member-title {
        width: 85px;
    }

    .member-lastname {
        width: calc(100% - 85px);
    }
</style>
